<template>
  <div class="kiosk-flow">
    <div v-if="loading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p>Loading...</p>
    </div>
    
    <component 
      v-else
      :is="currentStepComponent" 
      :config="currentStepConfig"
      :transaction-data="transactionData"
      @step-complete="handleStepComplete"
      @step-back="handleStepBack"
      @set-priority="$emit('set-priority', $event)"
    />
    
    <!-- Global Navigation Footer -->
    <div v-if="!loading" class="kiosk-navigation">
      <button 
        v-if="currentStepIndex > 0" 
        class="btn btn-outline-secondary btn-lg back-btn"
        @click="handleStepBack"
      >
        <i class="fas fa-arrow-left"></i> Back
      </button>
      
      <button 
        v-if="currentStepIndex > 0"
        class="btn btn-danger btn-lg exit-btn"
        @click="exitKiosk"
      >
        <i class="fas fa-times"></i> Exit
      </button>
    </div>
  </div>
</template>

<script>
// Import all step components
import Welcome from '@/views/kiosk/Welcome.vue';
import TransactionSelection from '@/views/kiosk/TransactionSelection.vue';
import StudentVerification from '@/views/kiosk/StudentVerification.vue';
import AmountInput from '@/views/kiosk/AmountInput.vue';
import QueueNumber from '@/views/kiosk/QueueNumber.vue';
import StudentId from '@/views/kiosk/StudentId.vue';
import { kioskFlowApi } from '@/services/api';
import { Modal } from 'bootstrap';
import { mapGetters } from 'vuex';

export default {
  name: 'KioskFlowOrchestrator',
  components: {
    KioskWelcome: Welcome,
    TransactionSelection,
    StudentVerification,
    AmountInput,
    QueueNumber,
    StudentId
  },
  data() {
    return {
      loading: true,
      flowConfig: null,
      currentStepIndex: 0,
      transactionData: {},
      flowHistory: [],
      exitModal: null
    };
  },
  computed: {
    ...mapGetters({
      selectedTransactionType: 'kiosk/getSelectedTransactionType',
    }),
    currentStep() {
      if (!this.flowConfig || !this.flowConfig.steps || this.flowConfig.steps.length === 0) {
        return null;
      }
      return this.flowConfig.steps[this.currentStepIndex];
    },
    currentStepComponent() {
      if (!this.currentStep) return null;
      
      // Map component name to the registered component
      switch (this.currentStep.component) {
        case 'Welcome':
          return 'KioskWelcome';
        case 'TransactionSelection':
        case 'StudentVerification':
        case 'AmountInput':
        case 'QueueNumber':
        case 'StudentId':
          return this.currentStep.component;
        default:
          console.error(`Unknown component: ${this.currentStep.component}`);
          return null;
      }
    },
    currentStepConfig() {
      return this.currentStep ? this.currentStep.config || {} : {};
    }
  },
  watch: {
    // Watch for changes to the selected transaction type
    selectedTransactionType: {
      handler(newType) {
        if (newType && newType.flow_config && Array.isArray(newType.flow_config.steps)) {
          console.log('Using transaction-specific flow for:', newType.display_name);
          // Use the transaction-specific flow configuration
          this.updateFlowConfig(newType.flow_config);
        }
      },
      deep: true
    }
  },
  created() {
    this.loadFlowConfig();
  },
  mounted() {
    // Create exit confirmation modal
    const modalHtml = `
      <div class="modal fade" id="exitConfirmModal" tabindex="-1" aria-labelledby="exitConfirmModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="exitConfirmModalLabel">Exit Kiosk</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <p>Are you sure you want to exit? Your current transaction will be lost.</p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="btn btn-danger" id="confirmExitBtn">Yes, Exit</button>
            </div>
          </div>
        </div>
      </div>
    `;
    
    // Append modal to body
    const modalDiv = document.createElement('div');
    modalDiv.innerHTML = modalHtml;
    document.body.appendChild(modalDiv);
    
    // Initialize modal
    this.exitModal = new Modal(document.getElementById('exitConfirmModal'));
    
    // Add event listener to confirm button
    document.getElementById('confirmExitBtn').addEventListener('click', () => {
      this.resetAndGoToStart();
      this.exitModal.hide();
    });
  },
  methods: {
    async loadFlowConfig() {
      try {
        console.log('Loading flow configuration...');
        // Load flow configuration from API
        const response = await kioskFlowApi.getFlowConfig();
        console.log('Raw flow config loaded:', response.data);
        
        let configData = response.data;
        
        // Handle case where response is a string (JSON)
        if (typeof configData === 'string') {
          try {
            configData = JSON.parse(configData);
            console.log('Parsed JSON flow config:', configData);
          } catch (parseError) {
            console.error('Error parsing flow config JSON:', parseError);
            throw new Error('Invalid flow configuration format');
          }
        }
        
        if (configData && Array.isArray(configData.steps)) {
          // Use the configuration from the API
          this.flowConfig = {
            steps: configData.steps,
            conditionalLogic: Array.isArray(configData.conditionalLogic) 
              ? configData.conditionalLogic 
              : []
          };
          
          // Ensure the flow has all required components
          this.ensureCorrectFlowSequence();
        } else {
          throw new Error('Invalid flow configuration format');
        }
      } catch (error) {
        console.error('Failed to load flow configuration:', error);
        // Load default configuration if API fails
        this.flowConfig = this.getDefaultFlowConfig();
      } finally {
        this.loading = false;
      }
    },
    
    ensureCorrectFlowSequence() {
      // Define the required components (regardless of order)
      const requiredComponents = [
        { id: 'welcome', component: 'Welcome' },
        { id: 'transaction_selection', component: 'TransactionSelection' },
        { id: 'queue_number', component: 'QueueNumber' }
      ];
      
      // Check if all required components are present (order doesn't matter)
      const existingComponents = this.flowConfig.steps.map(step => step.component);
      
      // If any required component is missing, rebuild the flow
      const missingComponents = requiredComponents.filter(
        req => !existingComponents.includes(req.component)
      );
      
      if (missingComponents.length > 0) {
        console.warn('Missing required components in flow configuration:', missingComponents);
        this.flowConfig = this.getDefaultFlowConfig();
      }
      
      // No longer enforcing a specific order - using the order from the database
      console.log('Using flow order from database configuration:', 
        this.flowConfig.steps.map(step => step.component).join(' → ')
      );
    },
    
    getDefaultFlowConfig() {
      return {
        steps: [
          {
            id: 'welcome',
            component: 'Welcome',
            required: true,
            config: { showLogo: true, animationEnabled: true }
          },
          {
            id: 'transaction_selection',
            component: 'TransactionSelection',
            required: true,
            config: { categorized: true, showPopular: true }
          },
          {
            id: 'student_id',
            component: 'StudentId',
            required: true,
            config: { allowManualEntry: true }
          },
          {
            id: 'amount_input',
            component: 'AmountInput',
            required: true,
            config: {}
          },
          {
            id: 'student_verification',
            component: 'StudentVerification',
            required: true,
            config: { verificationMethod: 'id_number', allowManualEntry: true }
          },
          {
            id: 'queue_number',
            component: 'QueueNumber',
            required: true,
            config: { printTicket: true, showEstimatedTime: true }
          }
        ],
        conditionalLogic: []
      };
    },
    
    handleStepComplete(data) {
      console.log('Step complete with data:', data);
      
      // Check if we need to go back to welcome screen (from QueueNumber)
      if (data && data.goToWelcome) {
        // Reset all data
        this.resetAndGoToStart();
        return;
      }
      
      // Save step data
      this.transactionData = { ...this.transactionData, ...data };
      
      // Ensure skipVerification is preserved if it was set
      if (data && data.skipVerification) {
        this.transactionData.skipVerification = true;
      }
      
      // If this step was StudentId, we need to make sure the student data is loaded
      // before proceeding to the next step
      if (this.currentStep && this.currentStep.component === 'StudentId' && data.studentId) {
        // Store the student ID in Vuex store so StudentVerification can access it
        this.$store.dispatch('kiosk/fetchStudentByIdNumber', data.studentId)
          .then(() => {
            this.proceedToNextStep();
          })
          .catch(error => {
            console.error('Error fetching student data:', error);
            // Don't proceed if student data couldn't be fetched
            // Show an error message to the user
            alert('Unable to verify student ID. Please try again.');
            return;
          });
      } else {
        this.proceedToNextStep();
      }
    },
    
    proceedToNextStep() {
      // Save current step to history for back navigation
      this.flowHistory.push(this.currentStepIndex);
      
      // Check for conditional logic
      const nextStep = this.determineNextStep();
      
      // Move to next step
      if (nextStep !== null) {
        this.currentStepIndex = nextStep;
      } else {
        // Default to next step in sequence
        let nextIndex = this.currentStepIndex + 1;
        
        // Skip verification steps if skipVerification is true
        if (this.transactionData.skipVerification) {
          while (
            nextIndex < this.flowConfig.steps.length &&
            (this.flowConfig.steps[nextIndex].component === 'StudentId' || 
             this.flowConfig.steps[nextIndex].component === 'StudentVerification')
          ) {
            nextIndex++;
          }
        }
        
        if (nextIndex < this.flowConfig.steps.length) {
          this.currentStepIndex = nextIndex;
        } else {
          console.log('Reached end of flow');
        }
      }
    },
    
    handleStepBack() {
      if (this.flowHistory.length > 0) {
        // Go back to previous step from history
        this.currentStepIndex = this.flowHistory.pop();
      } else {
        // Default to previous step in sequence
        this.currentStepIndex = Math.max(0, this.currentStepIndex - 1);
      }
    },
    
    exitKiosk() {
      // Show exit confirmation modal
      this.exitModal.show();
    },
    
    resetAndGoToStart() {
      // Reset all data
      this.transactionData = {};
      this.flowHistory = [];
      this.currentStepIndex = 0;
      
      // Reset store data
      this.$store.dispatch('kiosk/resetKiosk');
    },
    
    determineNextStep() {
      // Check if we have conditional logic defined
      if (!this.flowConfig.conditionalLogic || !this.flowConfig.conditionalLogic.length) {
        return null;
      }
      
      // Evaluate each condition
      for (const logic of this.flowConfig.conditionalLogic) {
        // Simple condition evaluation (can be expanded for more complex logic)
        const condition = logic.if;
        let result = false;
        
        try {
          // Example: "transaction_type == 'payment'"
          if (condition.includes('==')) {
            const [left, right] = condition.split('==').map(part => part.trim());
            const leftValue = this.getNestedValue(this.transactionData, left);
            const rightValue = right.replace(/['"]/g, '');
            result = leftValue == rightValue;
          } else if (condition.includes('!=')) {
            const [left, right] = condition.split('!=').map(part => part.trim());
            const leftValue = this.getNestedValue(this.transactionData, left);
            const rightValue = right.replace(/['"]/g, '');
            result = leftValue != rightValue;
          }
          
          // If condition is true, return the specified next step index
          if (result) {
            const nextStepId = logic.then;
            return this.getStepIndexById(nextStepId);
          } else if (logic.else) {
            return this.getStepIndexById(logic.else);
          }
        } catch (error) {
          console.error('Error evaluating condition:', condition, error);
        }
      }
      
      return null;
    },
    
    getStepIndexById(stepId) {
      return this.flowConfig.steps.findIndex(step => step.id === stepId);
    },
    
    getNestedValue(obj, path) {
      return path.split('.').reduce((o, key) => o && o[key], obj);
    },
    
    updateFlowConfig(newConfig) {
      this.flowConfig = {
        steps: newConfig.steps,
        conditionalLogic: Array.isArray(newConfig.conditionalLogic) 
          ? newConfig.conditionalLogic 
          : []
      };
      
      // Ensure the flow has all required components
      this.ensureCorrectFlowSequence();
    }
  }
};
</script>

<style scoped>
.kiosk-flow {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.kiosk-navigation {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  z-index: 1000;
}

.back-btn, .exit-btn {
  padding: 0.75rem 2rem;
  font-size: 1.2rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.back-btn:hover, .exit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.exit-btn {
  background-color: #dc3545;
  border-color: #dc3545;
  color: white;
}

@media screen and (orientation: portrait) {
  .kiosk-navigation {
    padding: 1.5rem;
  }
  
  .back-btn, .exit-btn {
    padding: 1rem 2.5rem;
    font-size: 1.5rem;
  }
}
</style>
